import {
  HttpClient,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpParams,
  HttpRequest,
} from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { getCellApiUrl, routerCellName } from '@rivethealth/data/cell-resolver';
import { BehaviorSubject, forkJoin, map, Observable } from 'rxjs';
import { ENVIRONMENT } from '../common/core/environment';
import { API_CELL_URL_TEMPLATE, API_URL } from './common';

@Injectable({ providedIn: 'root' })
export class CellStore {
  private cell: string | undefined = undefined;
  private cells: string[] = [];
  constructor(
    @Inject(API_CELL_URL_TEMPLATE) private readonly apiCellUrlTemplate: string,
    @Inject(ENVIRONMENT) private readonly environment: string,
  ) {}

  /** Same data as `CellStore.getCell()` but compatible with RxJS patterns */
  readonly cellSubject = new BehaviorSubject<typeof this.cell>(this.cell);
  /** Same data as `CellStore.getCells()` but compatible with RxJS patterns */
  readonly cellsSubject = new BehaviorSubject<typeof this.cells>(this.cells);

  setCell(cell: string | undefined) {
    if (cell == this.cell) {
      // eslint-disable-next-line no-console
      console.log(`Keeping cell as '${cell}' for API requests`);
    } else {
      // eslint-disable-next-line no-console
      console.log(`Setting cell to '${cell}' for API requests`);
    }
    // You can `console.trace()` here if you need to figure out who called this
    this.cell = cell;
    this.cellSubject.next(cell);
  }

  setCells(cells: string[]) {
    this.cells = cells;
    this.cellsSubject.next(cells);
  }

  getCell(): string | undefined {
    return this.cell;
  }

  getCells(): string[] {
    return this.cells;
  }

  getCellUrl(): string | undefined {
    if (!this.cell || this.cell === routerCellName) {
      // return undefined if account has no cell
      return undefined;
    } else {
      return this.getUrl(this.cell);
    }
  }

  getUrl(cell: string): string {
    return getCellApiUrl(this.environment, cell, this.apiCellUrlTemplate);
  }

  setCellByAccountId(
    /** Can use accounts array of type (ExistingAccount & { cell: string })[] */
    accounts: { id: number | string; cell: string }[],
    accountId: number | string,
  ) {
    const account = accounts.find(account => account.id == accountId);
    if (account == null) {
      throw new Error(
        `setCellByAccountId: Couldn't find account id ${accountId} in accounts list supplied}`,
      );
    }
    this.setCell(account.cell);
  }
}

type HttpRequestParams =
  | HttpParams
  | {
      [param: string]:
        | string
        | number
        | boolean
        | ReadonlyArray<string | number | boolean>;
    };

@Injectable({ providedIn: 'root' })
export class AdminCellApiTranslator {
  constructor(
    private readonly cellStore: CellStore,
    private readonly http: HttpClient,
    @Inject(API_URL) private readonly apiUrl: string,
  ) {}

  replaceUrl(url: string, cell: string) {
    return url.replace(this.apiUrl, this.cellStore.getUrl(cell));
  }

  getAll<T>(url: string, params?: HttpRequestParams) {
    return forkJoin(
      this.cellStore
        .getCells()
        .map(cell =>
          this.http
            .get<T>(this.replaceUrl(url, cell), { params })
            .pipe(map(response => ({ response, cell: cell }))),
        ),
    );
  }
}

/**
 * Inject cell api url
 */
@Injectable({ providedIn: 'root' })
export class CellUrlInterceptor implements HttpInterceptor {
  constructor(
    @Inject(API_URL) private readonly apiUrl: string,
    private readonly cellStore: CellStore,
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const cellUrl = this.cellStore.getCellUrl();
    if (cellUrl) {
      req = req.clone({
        url: req.url.replace(this.apiUrl, cellUrl),
      });
    }
    return next.handle(req);
  }
}
